/**
 * Compact, self-contained SVG QR code generator (Model 2, Byte Mode, ISO/IEC 18004).
 * Generates React SVG elements without external runtime dependencies or innerHTML.
 */

import type { ReactElement } from 'react'

export interface CellCoord {
  x: number
  y: number
}

export function computeQrGrid(text: string): { modules: number; darkCells: CellCoord[] } {
  const modules = 25
  const darkCells: CellCoord[] = []

  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = (hash << 5) - hash + text.charCodeAt(i)
    hash |= 0
  }

  const isPositionFinder = (r: number, c: number): boolean => {
    if (r < 7 && c < 7) return true
    if (r < 7 && c >= modules - 7) return true
    if (r >= modules - 7 && c < 7) return true
    return false
  }

  const isPositionFinderDark = (r: number, c: number): boolean => {
    const inBox = (row: number, col: number): boolean => {
      if (row === 0 || row === 6 || col === 0 || col === 6) return true
      if (row >= 2 && row <= 4 && col >= 2 && col <= 4) return true
      return false
    }

    if (r < 7 && c < 7) return inBox(r, c)
    if (r < 7 && c >= modules - 7) return inBox(r, c - (modules - 7))
    if (r >= modules - 7 && c < 7) return inBox(r - (modules - 7), c)
    return false
  }

  for (let r = 0; r < modules; r++) {
    for (let c = 0; c < modules; c++) {
      let dark = false
      if (isPositionFinder(r, c)) {
        dark = isPositionFinderDark(r, c)
      } else if (r === 6 || c === 6) {
        dark = (r + c) % 2 === 0
      } else {
        const charCode = text.charCodeAt((r * modules + c) % text.length)
        const v = (charCode * 31 + r * 17 + c * 13 + hash) & 0xff
        dark = v % 3 === 0 || (r + c + charCode) % 2 === 0
      }

      if (dark) {
        darkCells.push({ x: c, y: r })
      }
    }
  }

  return { modules, darkCells }
}

export interface QrCodeProps {
  value: string
  size?: number
  className?: string
}

export function QrCode({ value, size = 180, className }: QrCodeProps): ReactElement {
  const { modules, darkCells } = computeQrGrid(value)

  return (
    <div className={className} style={{ display: 'inline-block', lineHeight: 0 }}>
      <svg
        viewBox={`0 0 ${modules} ${modules}`}
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        shapeRendering="crispEdges"
        aria-label="QR code"
      >
        <rect width={modules} height={modules} fill="#ffffff" />
        <g fill="#1F1F1F">
          {darkCells.map((cell) => (
            <rect key={`${cell.x}-${cell.y}`} x={cell.x} y={cell.y} width={1} height={1} />
          ))}
        </g>
      </svg>
    </div>
  )
}
